import React, { useCallback, useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import useUser from '../../../hooks/useUser';
import { Spinner } from '../../../design-system';
import './DeploymentImpactWarning.css';

interface DeploymentImpactWarningProps {
  show: boolean;
}

interface HistoricalReturnPoint {
  portfolio_value?: number | string | null;
}

interface ActiveDeploymentResponse {
  id?: string;
  botId: string;
  name?: string | null;
  initialAccountValue?: number | null;
  currentAccountValue?: number | null;
}

interface DeploymentPerformance {
  botName: string;
  currentAccountValue: number;
  initialAccountValue: number;
  realPerformanceAvailable: boolean;
  historicalDataPoints?: number;
}

type DeploymentWithPerformance = ActiveDeploymentResponse & DeploymentPerformance;

const asFiniteNumber = (value: number | string | null | undefined): number => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(parsed) ? Number(parsed) : 0;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercentage = (percent?: number | null): string => {
  if (percent === undefined || percent === null || !isFinite(percent)) return '0.0%';
  return `${percent >= 0 ? '+' : ''}${Number(percent).toFixed(1)}%`;
};

const calculateGainLoss = (initial: number, current: number): { dollarGain: number; percentGain: number } => {
  if (!initial || initial === 0) return { dollarGain: 0, percentGain: 0 };

  const dollarGain = current - initial;
  const percentGain = (dollarGain / initial) * 100;

  return { dollarGain, percentGain };
};

const buildHistoricalReturnsQuery = (botId: string): string => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(startDate.getFullYear() - 2);

  return new URLSearchParams({
    botId,
    start_timestamp: String(Math.floor(startDate.getTime() / 1000)),
    end_timestamp: String(Math.floor(endDate.getTime() / 1000)),
    frequency: 'day',
    limit: '1000',
    sort: 'desc'
  }).toString();
};

const getPerformanceBaseline = (
  historicalData: HistoricalReturnPoint[],
  latestPortfolioValue: number,
  deploymentInitialValue?: number | null,
): { initialValue: number; realPerformanceAvailable: boolean } => {
  if (!historicalData.length) {
    return {
      initialValue: latestPortfolioValue,
      realPerformanceAvailable: false
    };
  }

  const oldestDataPoint = historicalData[historicalData.length - 1];
  const earliestValue = asFiniteNumber(oldestDataPoint?.portfolio_value);

  if (earliestValue > 0) {
    return {
      initialValue: earliestValue,
      realPerformanceAvailable: true
    };
  }

  if (deploymentInitialValue && deploymentInitialValue > 0) {
    return {
      initialValue: deploymentInitialValue,
      realPerformanceAvailable: true
    };
  }

  return {
    initialValue: latestPortfolioValue,
    realPerformanceAvailable: false
  };
};

const DeploymentImpactWarning = ({ show }: DeploymentImpactWarningProps): React.ReactElement | null => {
  const { getApiToken } = useUser();
  const [deployments, setDeployments] = useState<DeploymentWithPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRealPerformanceData = useCallback(async (
    deployment: ActiveDeploymentResponse,
    token: string,
  ): Promise<DeploymentPerformance> => {
    try {
      const botResponse = await fetch(`${process.env.REACT_APP_API_URL}/bots/${deployment.botId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let botName = deployment.name || 'Trading Bot';
      if (botResponse.ok) {
        const botData = await botResponse.json();
        botName = botData.name || botName;
      }

      const query = buildHistoricalReturnsQuery(deployment.botId);
      const perfResponse = await fetch(`${process.env.REACT_APP_API_URL}/historical-returns?${query}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!perfResponse.ok) {
        return {
          botName,
          currentAccountValue: deployment.currentAccountValue || deployment.initialAccountValue || 0,
          initialAccountValue: deployment.initialAccountValue || 0,
          realPerformanceAvailable: false
        };
      }

      const perfData = await perfResponse.json();

      if (perfData.is_new_bot || !perfData.items || perfData.items.length === 0) {
        return {
          botName,
          currentAccountValue: perfData.latest_portfolio_value || deployment.initialAccountValue || 0,
          initialAccountValue: deployment.initialAccountValue || 0,
          realPerformanceAvailable: false
        };
      }

      const latestPortfolioValue = asFiniteNumber(perfData.latest_portfolio_value);
      const historicalData: HistoricalReturnPoint[] = Array.isArray(perfData.items) ? perfData.items : [];
      const { initialValue, realPerformanceAvailable } = getPerformanceBaseline(
        historicalData,
        latestPortfolioValue,
        deployment.initialAccountValue
      );

      return {
        botName,
        currentAccountValue: latestPortfolioValue,
        initialAccountValue: initialValue,
        realPerformanceAvailable,
        historicalDataPoints: historicalData.length
      };
    } catch {
      return {
        botName: deployment.name || 'Trading Bot',
        currentAccountValue: deployment.currentAccountValue || deployment.initialAccountValue || 0,
        initialAccountValue: deployment.initialAccountValue || 0,
        realPerformanceAvailable: false
      };
    }
  }, []);

  const fetchActiveDeployments = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

      const token = await getApiToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/deploy/active-deployments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch deployments: ${response.status}`);
      }

      const data = await response.json();

      if (!data.deployments || data.deployments.length === 0) {
        setDeployments([]);
        return;
      }

      const deploymentsPayload: ActiveDeploymentResponse[] = Array.isArray(data.deployments) ? data.deployments : [];
      const deploymentsWithPerformance = await Promise.all(
        deploymentsPayload.map(async (deployment) => {
          const perfData = await fetchRealPerformanceData(deployment, token);
          return {
            ...deployment,
            ...perfData
          };
        })
      );

      setDeployments(deploymentsWithPerformance);
    } catch {
      setError('Unable to load deployment information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchRealPerformanceData, getApiToken]);

  useEffect(() => {
    if (show) {
      fetchActiveDeployments();
    }
  }, [fetchActiveDeployments, show]);

  if (!show) return null;

  if (loading) {
    return (
      <div className="deployment-impact-loading account-settings-loading-state account-settings-loading-state--panel">
        <Spinner label="Checking active deployments" />
        <span>Checking your active deployments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="deployment-impact-alert deployment-impact-alert--warning">
        <FontAwesomeIcon icon={faExclamationTriangle} className="deployment-impact-alert-icon" />
        {error}
      </Alert>
    );
  }

  if (!deployments || deployments.length === 0) {
    return (
      <div className="deployment-impact-none">
        <Alert variant="info" className="deployment-impact-alert deployment-impact-alert--info">
          <FontAwesomeIcon icon={faInfoCircle} className="deployment-impact-alert-icon" />
          <div>
            <strong>No Active Deployments</strong>
            <p>You don't have any running deployments, so canceling won't affect any active trades.</p>
          </div>
        </Alert>
      </div>
    );
  }

  const totalInitial = deployments.reduce((sum, dep) => sum + (dep.initialAccountValue || 0), 0);
  const totalCurrent = deployments.reduce((sum, dep) => sum + (dep.currentAccountValue || 0), 0);
  const totalGainLoss = calculateGainLoss(totalInitial, totalCurrent);
  const isProfit = totalGainLoss.dollarGain >= 0;

  return (
    <div className="deployment-impact-warning">
      <Alert
        variant={isProfit ? 'success' : 'warning'}
        className={`deployment-impact-panel ${isProfit ? 'deployment-impact-panel--positive' : 'deployment-impact-panel--attention'}`}
      >
        <div className="deployment-impact-summary">
          <FontAwesomeIcon icon={faChartLine} className="deployment-impact-summary-icon" />
          <strong>
            {deployments.length} Active Deployment{deployments.length !== 1 ? 's' : ''}
          </strong>
        </div>

        <div className="deployment-impact-bot-list">
          {deployments.map((deployment, index) => {
            const gainLoss = calculateGainLoss(deployment.initialAccountValue, deployment.currentAccountValue);
            const botIsProfit = gainLoss.dollarGain >= 0;

            return (
              <article key={deployment.id || index} className="deployment-impact-bot">
                <div className="deployment-impact-bot-header">
                  <strong>{deployment.botName}</strong>
                  {deployment.realPerformanceAvailable && (
                    <span className="deployment-impact-data-label">Real data</span>
                  )}
                </div>

                <dl className="deployment-impact-details">
                  <div className="deployment-impact-detail">
                    <dt>Account value</dt>
                    <dd><strong>{formatCurrency(deployment.currentAccountValue)}</strong></dd>
                  </div>
                  <div className="deployment-impact-detail">
                    <dt>Gain/loss</dt>
                    <dd>
                      <strong className={`deployment-gain-loss ${botIsProfit ? 'profit' : 'loss'}`}>
                        {botIsProfit ? '+' : ''}{formatCurrency(Math.abs(gainLoss.dollarGain))}
                      </strong>
                    </dd>
                  </div>
                  <div className="deployment-impact-detail">
                    <dt>Gain/loss percent</dt>
                    <dd>
                      <strong className={`deployment-gain-loss ${botIsProfit ? 'profit' : 'loss'}`}>
                        {formatPercentage(gainLoss.percentGain)}
                      </strong>
                    </dd>
                  </div>
                </dl>

                {deployment.historicalDataPoints && (
                  <p className="deployment-impact-data-note">
                    Based on {deployment.historicalDataPoints} real data points
                  </p>
                )}
              </article>
            );
          })}
        </div>

        {deployments.length > 1 && (
          <div className="deployment-impact-total">
            <strong>Combined total</strong>
            <dl className="deployment-impact-details">
              <div className="deployment-impact-detail">
                <dt>Total account value</dt>
                <dd><strong>{formatCurrency(totalCurrent)}</strong></dd>
              </div>
              <div className="deployment-impact-detail">
                <dt>Total gain/loss</dt>
                <dd>
                  <strong className={`deployment-gain-loss ${isProfit ? 'profit' : 'loss'}`}>
                    {isProfit ? '+' : ''}{formatCurrency(Math.abs(totalGainLoss.dollarGain))}
                  </strong>
                </dd>
              </div>
              <div className="deployment-impact-detail">
                <dt>Total gain/loss percent</dt>
                <dd>
                  <strong className={`deployment-gain-loss ${isProfit ? 'profit' : 'loss'}`}>
                    {formatPercentage(totalGainLoss.percentGain)}
                  </strong>
                </dd>
              </div>
            </dl>
          </div>
        )}
      </Alert>

      <Alert variant="info" className="deployment-impact-question">
        <h5>Are you sure you want to cancel?</h5>
        {isProfit && totalGainLoss.dollarGain > 0 && (
          <p className="profit-warning">
            You'll stop earning from your profitable {deployments.length === 1 ? 'strategy' : 'strategies'}
          </p>
        )}
      </Alert>
    </div>
  );
};

export default DeploymentImpactWarning;
